/**
 * Formatação e validação de documentos BR (CNPJ, CPF).
 *
 * Funções `formatX` aceitam string crua ou com máscara, retornam formatada.
 * Funções `validateX` retornam boolean (validação completa com dígitos verificadores).
 * Funções `maskX` aplicam máscara conforme o usuário digita (útil em onChange).
 */

// Remove tudo que não é dígito
function onlyDigits(s: string): string {
  return s.replace(/\D/g, '');
}

// ============================================================
// CNPJ
// ============================================================

/**
 * Formata CNPJ com máscara.
 *
 * formatCNPJ('12345678000190')       → "12.345.678/0001-90"
 * formatCNPJ('12.345.678/0001-90')   → "12.345.678/0001-90"
 * formatCNPJ('invalid')              → "—"
 */
export function formatCNPJ(input: string | null | undefined): string {
  if (!input) return '—';
  const d = onlyDigits(input);
  if (d.length !== 14) return '—';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
}

/**
 * Aplica máscara parcial enquanto usuário digita (uso em onChange de input).
 *
 * maskCNPJ('1234') → "12.34"
 * maskCNPJ('12345678000190') → "12.345.678/0001-90"
 */
export function maskCNPJ(input: string): string {
  const d = onlyDigits(input).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/**
 * Valida CNPJ com cálculo de dígitos verificadores.
 *
 * isValidCNPJ('11.222.333/0001-81') → true se DV bater
 * isValidCNPJ('11111111111111')     → false (sequência inválida)
 */
export function isValidCNPJ(input: string | null | undefined): boolean {
  if (!input) return false;
  const cnpj = onlyDigits(input);
  if (cnpj.length !== 14) return false;
  // Rejeita sequências repetidas (00000000000000, 11111111111111, etc.)
  if (/^(\d)\1+$/.test(cnpj)) return false;

  // Cálculo dos dígitos verificadores
  const calc = (digits: string, weights: number[]) => {
    const sum = digits.split('').reduce((acc, d, i) => acc + Number(d) * weights[i], 0);
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };

  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const dv1 = calc(cnpj.slice(0, 12), w1);
  if (dv1 !== Number(cnpj[12])) return false;
  const dv2 = calc(cnpj.slice(0, 13), w2);
  return dv2 === Number(cnpj[13]);
}

// ============================================================
// CPF
// ============================================================

/**
 * Formata CPF com máscara.
 *
 * formatCPF('12345678901')      → "123.456.789-01"
 * formatCPF('123.456.789-01')   → "123.456.789-01"
 */
export function formatCPF(input: string | null | undefined): string {
  if (!input) return '—';
  const d = onlyDigits(input);
  if (d.length !== 11) return '—';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}

/** Máscara progressiva pra input controlado. */
export function maskCPF(input: string): string {
  const d = onlyDigits(input).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/**
 * Valida CPF com cálculo de dígitos verificadores.
 */
export function isValidCPF(input: string | null | undefined): boolean {
  if (!input) return false;
  const cpf = onlyDigits(input);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false;

  const calc = (digits: string, factor: number) => {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      sum += Number(digits[i]) * (factor - i);
    }
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  const dv1 = calc(cpf.slice(0, 9), 10);
  if (dv1 !== Number(cpf[9])) return false;
  const dv2 = calc(cpf.slice(0, 10), 11);
  return dv2 === Number(cpf[10]);
}

// ============================================================
// CNPJ ou CPF — auto-detect
// ============================================================

/**
 * Formata CNPJ ou CPF baseado no tamanho da string.
 *
 * formatDocument('12345678901')       → "123.456.789-01"
 * formatDocument('12345678000190')    → "12.345.678/0001-90"
 */
export function formatDocument(input: string | null | undefined): string {
  if (!input) return '—';
  const d = onlyDigits(input);
  if (d.length === 11) return formatCPF(d);
  if (d.length === 14) return formatCNPJ(d);
  return '—';
}
